// @ts-nocheck
import { forwardRef } from 'react';
import {
  Box,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import { pickTextColorForBackground } from '../../../../utils/color';
import type { HeatmapWidgetProps } from '../types';

/**
 * HeatmapWidget - Generic grid-based heatmap
 *
 * Displays intensity data in a grid format (e.g., hours × days of week).
 */
const HeatmapWidget = forwardRef<HTMLDivElement, HeatmapWidgetProps>(
  (
    {
      title,
      subtitle,
      data,
      colorScale = ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'],
      tooltipFormatter,
      cellSize = 20,
      cellGap = 2,
      showValues = false,
      xAxisLabel,
      yAxisLabel,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const isDark = colorScheme === 'dark';

    // Get unique x and y values
    const xValues = [...new Set(data.map((d) => d.x))].sort();
    const yValues = [...new Set(data.map((d) => d.y))].sort();

    // Find min/max values for color scaling
    const values = data.map((d) => d.value);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    // Get color for a value
    const getColor = (value: number) => {
      if (maxValue === minValue) return colorScale[Math.floor(colorScale.length / 2)];
      const normalized = (value - minValue) / (maxValue - minValue);
      const index = Math.floor(normalized * (colorScale.length - 1));
      return colorScale[index];
    };

    // Get cell data
    const getCellData = (x: string | number, y: string | number) => {
      return data.find((d) => d.x === x && d.y === y);
    };

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            {/* Header */}
            <Box>
              {title && <Text size="lg" fw={600}>{title}</Text>}
              {subtitle && <Text size="sm" c="dimmed">{subtitle}</Text>}
            </Box>

            {/* Heatmap grid */}
            <Box style={{ overflowX: 'auto' }}>
              <Box style={{ display: 'inline-block' }}>
                {/* Y-axis label */}
                {yAxisLabel && (
                  <Text size="xs" fw={500} mb="xs">
                    {yAxisLabel}
                  </Text>
                )}

                {/* Grid */}
                <Box style={{ display: 'flex', gap: cellGap }}>
                  {/* Y-axis */}
                  <Stack gap={cellGap} style={{ paddingTop: cellSize + cellGap }}>
                    {yValues.map((y) => (
                      <Box
                        key={String(y)}
                        style={{
                          height: cellSize,
                          display: 'flex',
                          alignItems: 'center',
                          paddingRight: 8,
                        }}
                      >
                        <Text size="xs" c="dimmed">
                          {y}
                        </Text>
                      </Box>
                    ))}
                  </Stack>

                  {/* Cells */}
                  <Box>
                    {/* X-axis */}
                    <Box style={{ display: 'flex', gap: cellGap, marginBottom: cellGap }}>
                      {xValues.map((x) => (
                        <Box
                          key={String(x)}
                          style={{
                            width: cellSize,
                            height: cellSize,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Text size="xs" c="dimmed">
                            {x}
                          </Text>
                        </Box>
                      ))}
                    </Box>

                    {/* Grid cells */}
                    <Stack gap={cellGap}>
                      {yValues.map((y) => (
                        <Box key={String(y)} style={{ display: 'flex', gap: cellGap }}>
                          {xValues.map((x) => {
                            const cellData = getCellData(x, y);
                            const cellValue = cellData?.value || 0;
                            const cellColor = getColor(cellValue);

                            // Calculate text color based on cell background
                            const cellTextColor = showValues ? pickTextColorForBackground(cellColor) : undefined;

                            return (
                              <Tooltip
                                key={`${x}-${y}`}
                                label={
                                  tooltipFormatter
                                    ? tooltipFormatter(cellData || { x, y, value: cellValue })
                                    : `${x}, ${y}: ${cellValue}`
                                }
                                withArrow
                              >
                                <Box
                                  style={{
                                    width: cellSize,
                                    height: cellSize,
                                    backgroundColor: cellColor,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'transform 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.1)',
                                    },
                                  }}
                                >
                                  {showValues && (
                                    <Text size="10px" fw={600} c={cellTextColor}>
                                      {cellValue}
                                    </Text>
                                  )}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>

                {/* X-axis label */}
                {xAxisLabel && (
                  <Text size="xs" fw={500} mt="xs" ta="center">
                    {xAxisLabel}
                  </Text>
                )}
              </Box>
            </Box>

            {/* Legend */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text size="xs" c="dimmed">Less</Text>
              <Box style={{ display: 'flex', gap: 2 }}>
                {colorScale.map((color, idx) => (
                  <Box
                    key={idx}
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: color,
                      borderRadius: 2,
                    }}
                  />
                ))}
              </Box>
              <Text size="xs" c="dimmed">More</Text>
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

HeatmapWidget.displayName = 'HeatmapWidget';

export default HeatmapWidget;
